// [PHASE B / #015 — B4-4/B4-5 / Rule 22]: Merge the three historical
//   roll-table stores into one display-ready list. Pre-extraction the
//   DMToolsPanel loot picker only read `customRollTables`, so any
//   table authored in the Roll Tables tab (which writes to
//   `rollTables`) was invisible — users saw the empty-state "Create
//   Roll Table" prompt and could not push their authored loot to
//   their party.
//
// Three stores, all valid producers:
//   - customRollTables[]: CustomRollTableDialog + Advanced Shop builder
//     (entries: [{min, max, item}, ...] with item refs)
//   - rollTables[]:        RollTablesTab editor
//     (entries: ['1-10: result text', ...] string array)
//   - journalRollTables[]: legacy journal-side store
//     (similar shape to rollTables)
//
// The DMToolsPanel rolling logic tolerates either shape (it picks an
//   index from `table.entries[]`); the receiving party-side handler
//   already deals with both. So merging on the producer side surfaces
//   every authored table in the loot picker without requiring a
//   data-shape migration.

/**
 * Merge the three roll-table stores, deduplicating by `id`. Adds a
 * `_source` tag to each entry so the UI can show which store the
 * table came from (useful for debugging the next time these stores
 * drift). The `_source` value reflects the FIRST store that contained
 * the id; later duplicates fold their source into a `_alsoIn` array.
 *
 * @param {Array} customRollTables
 * @param {Array} rollTables
 * @param {Array} journalRollTables
 * @returns {Array} merged tables with _source / _alsoIn metadata
 */
export function mergeRollTables(customRollTables = [], rollTables = [], journalRollTables = []) {
  const out = [];
  const byId = new Map();

  function addAll(arr, source) {
    if (!Array.isArray(arr)) return;
    for (const table of arr) {
      if (!table || typeof table !== 'object') continue;
      const id = table.id;
      if (id == null) {
        // No id — push as-is with source tag, can't dedupe.
        out.push({ ...table, _source: source });
        continue;
      }
      const existing = byId.get(id);
      if (existing) {
        // Already seen this id in an earlier store. Track that the
        //   table also exists in this store (for the `_alsoIn` chain)
        //   without overwriting the canonical entry.
        existing._alsoIn = existing._alsoIn || [];
        if (!existing._alsoIn.includes(source)) {
          existing._alsoIn.push(source);
        }
        continue;
      }
      const tagged = { ...table, _source: source };
      out.push(tagged);
      byId.set(id, tagged);
    }
  }

  // Order matters: shop-side `customRollTables` first because the
  //   shop builder is the most-trafficked author surface today.
  //   `rollTables` (RollTablesTab) second, then the legacy
  //   `journalRollTables` last.
  addAll(customRollTables, 'custom');
  addAll(rollTables, 'rollTables');
  addAll(journalRollTables, 'journal');

  return out;
}
